import useCategoryQueryHook from "@/shared/hooks/useCategoryQueryHook";
import useCountryConfigHook from "@/shared/hooks/useCountryConfigHook";
import useMenuItemHook from "@/shared/hooks/useMenuItemHook";
import useMenuItemTranslatePost from "@/shared/mutations/menu/useMenuItemTranslatePost";
import { cn } from "@/shared/utils/cn";
import { productStatus, targetsWithoutAll } from "@/shared/utils/constant";
import { MenuItems } from "@/shared/utils/enum";
import {
  allergenen,
  asArray,
  errorToast,
  getFieldByLang,
  getLabel,
} from "@/shared/utils/helpers";
import {
  Garnish,
  MenuDataTagKind,
  Product,
  VatBasic,
} from "@/shared/utils/interfaces";
import { ReactNode } from "react";
import { Col, Row } from "react-grid-system";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { arrayMove } from "react-sortable-hoc";
import Icon from "../Icon/Icon";
import InputColor from "../Input/InputColor";
import Textfield from "../Input/Textfield";
import TextfieldIntControlled from "../Input/TextfieldIntControlled";
import TextfieldWithAi from "../Input/TextfieldWithAi";
import CreatableSelect from "../Select/CreatableSelect";
import Select from "../Select/Select";
import SortableSelect from "../Select/SortableSelect";
import MenuDataTagFields from "./MenuDataTagFields";
import ProductFormSection from "./ProductFormSection";

interface Props {
  vats: VatBasic[];
  setFields: (data: any) => void;
  fields: any;
  defaultValue: Product;
  labels: any[];
  isShown?: boolean;
  /** Rendered at the top of the right column; enables the two-column layout. */
  aside?: ReactNode;
}

const portalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1000000 }),
};

const ProductModalStandard = ({
  vats,
  setFields,
  fields,
  defaultValue,
  labels,
  isShown = true,
  aside,
}: Props) => {
  const { t } = useTranslation();
  const { categories } = useCategoryQueryHook();
  const formContext = useFormContext();
  const { errors, register, control, getValues } = formContext;

  const { data: garnishes, isLoading } = useMenuItemHook<Garnish[]>(
    MenuItems.GARNISH
  );
  const { data: products, isLoading: productIsLoading } = useMenuItemHook<
    Product[]
  >(MenuItems.PRODUCT);
  const category = categories.find((c) => c?.item_id === fields?.parentId);
  const nameMutation = useMenuItemTranslatePost("name");
  const descriptionMutation = useMenuItemTranslatePost("description");

  // Both work from the standard name as it is typed now, the category is only context
  const generateWithAi = (
    mutation: typeof nameMutation | typeof descriptionMutation
  ) => {
    const name = getFieldByLang(getValues("name"))?.trim();
    if (!name) {
      errorToast("menu.fillNameFirst");
      return;
    }
    mutation.mutate({ category: getFieldByLang(category?.name), name });
  };
  const portalTarget = document.getElementById("root");
  const { currency } = useCountryConfigHook();

  return (
    <div
      className={cn(
        aside ? "grid items-start gap-8 lg:grid-cols-2" : "flex flex-col gap-8",
        !isShown && "hidden"
      )}
    >
      <div className="flex flex-col gap-8">
        <ProductFormSection
          icon="edit"
          title={t("menu.productPage.general")}
          description={t("menu.productPage.generalHint")}
        >
          <Row>
            <Col xs={12} md={6}>
              <div className="mb-[2rem]">
                <Select
                  label={t("category")}
                  options={categories ?? []}
                  onChange={(v) => {
                    setFields((draft) => {
                      draft.parentId = v.item_id;
                    });
                  }}
                  value={category}
                  getOptionLabel={(option) => getFieldByLang(option.name)}
                  getOptionValue={(option) => option.item_id}
                  optionLabel
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="mb-[2rem]">
                <Textfield
                  label={t("menu.code")}
                  type="string"
                  defaultValue={defaultValue?.code}
                  placeholder={t("menu.code")}
                  name="code"
                  inputLeftAddon={
                    <Icon
                      className="h-6 w-6 fill-text-secondary"
                      variants="barcode"
                    />
                  }
                  ref={register({})}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="mb-[2rem]">
                <TextfieldWithAi
                  label={t("name")}
                  required
                  placeholder={t("name")}
                  name="name"
                  validationMessage={t("standardMustBeFilled")}
                  defaultValue={defaultValue?.name}
                  value={nameMutation.data}
                  tooltipContent={t("menu.translateNameWithAi")}
                  onClick={() => generateWithAi(nameMutation)}
                  isPending={nameMutation.isPending}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="mb-[2rem]">
                <TextfieldIntControlled
                  label={t("nameOnReceipt")}
                  placeholder={t("nameOnReceipt")}
                  name="name_on_receipt"
                  validationMessage={t("standardMustBeFilled")}
                  defaultValue={defaultValue?.name_on_receipt ?? {}}
                />
              </div>
            </Col>
            <Col xs={12}>
              <TextfieldWithAi
                label={t("description")}
                placeholder={t("menu.productDescription")}
                name="description"
                defaultValue={defaultValue?.description}
                value={descriptionMutation.data}
                tooltipContent={t("menu.generateDescWithAi")}
                onClick={() => generateWithAi(descriptionMutation)}
                isPending={descriptionMutation.isPending}
              />
            </Col>
          </Row>
          <div className="my-3"></div>
        </ProductFormSection>

        <ProductFormSection
          icon="money"
          title={t("menu.productPage.pricing")}
          description={t("menu.productPage.pricingHint")}
        >
          <Row>
            <Col xs={12} md={4}>
              <div className="mb-[2rem] md:mb-0">
                <Textfield
                  label={t("price")}
                  type="number"
                  step=".01"
                  defaultValue={defaultValue?.price}
                  placeholder={t("menu.productPrice")}
                  name="price"
                  inputLeftAddon={currency?.symbol}
                  error={errors?.price?.message}
                  ref={register({
                    required: {
                      value: true,
                      message: t("fieldRequired"),
                    },
                  })}
                />
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="mb-[2rem] md:mb-0">
                <Select
                  label={t("vat")}
                  menuPortalTarget={portalTarget}
                  styles={portalStyles}
                  name="btw"
                  onChange={(v) =>
                    setFields((draft) => {
                      draft.vat_enum = v.id;
                    })
                  }
                  options={vats}
                  value={vats.find((v) => v.id === fields?.vat_enum)}
                  getOptionLabel={(option) => t(option.label)}
                  getOptionValue={(option) => option.id}
                />
              </div>
            </Col>
            <Col xs={12} md={4}>
              <Textfield
                label={t("preparationTime")}
                type="number"
                defaultValue={defaultValue?.preparation_time}
                placeholder={t("preparationTimePlaceholder")}
                name="preparation_time"
                inputLeftAddon={
                  <Icon
                    className="h-6 w-6 fill-text-secondary"
                    variants="calender"
                  />
                }
                ref={register({
                  valueAsNumber: true,
                })}
              />
            </Col>
          </Row>
        </ProductFormSection>

        <ProductFormSection
          icon="listCheck"
          title={t("menu.productPage.availability")}
          description={t("menu.productPage.availabilityHint")}
        >
          <Row>
            <Col xs={12} md={6}>
              <div className="mb-[2rem] md:mb-0">
                <Select
                  menuPortalTarget={portalTarget}
                  styles={portalStyles}
                  label={t("status")}
                  name="status"
                  placeholder={t("status")}
                  getOptionValue={(option) => option}
                  getOptionLabel={({ label }) => t(label)}
                  value={fields.status_enum}
                  onChange={(v) =>
                    setFields((draft) => {
                      draft.status_enum = v;
                    })
                  }
                  options={productStatus}
                  optionLabel
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <Select
                menuPortalTarget={portalTarget}
                styles={portalStyles}
                label={t("targets")}
                name="target"
                placeholder={t("target")}
                value={fields.targets}
                isMulti
                onChange={(v) =>
                  setFields((draft) => {
                    draft.targets = v;
                  })
                }
                options={targetsWithoutAll}
                getOptionLabel={(option) => t(`${option.label}`)}
              />
            </Col>
          </Row>
        </ProductFormSection>
      </div>

      <div className="flex flex-col gap-8">
        {aside}

        <ProductFormSection
          icon="tag"
          title={t("menu.productPage.allergensAndTags")}
          description={t("menu.productPage.allergensAndTagsHint")}
        >
          <Row>
            <Col xs={12} md={6}>
              <div className="mb-[2rem] md:mb-0">
                <SortableSelect
                  key={Date.now()}
                  onSortEnd={({ oldIndex, newIndex }) => {
                    const newValue = arrayMove(
                      fields.allergen,
                      oldIndex,
                      newIndex
                    );
                    setFields((draft) => {
                      draft.allergen = newValue;
                    });
                  }}
                  styles={portalStyles}
                  menuPortalTarget={portalTarget}
                  isMulti
                  label={t("allergies")}
                  name="allergies"
                  placeholder={`${t("allergies")}....`}
                  getOptionLabel={(option) => t(option.translationKey)}
                  getOptionValue={(option) => option.id}
                  value={
                    fields.allergen ||
                    asArray(defaultValue?.allergen).map((al) =>
                      allergenen.find((a) => a.id === al)
                    )
                  }
                  onChange={(v) =>
                    setFields((draft) => {
                      draft.allergen = v;
                    })
                  }
                  defaultValue={[]}
                  options={allergenen}
                  optionLabel
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <CreatableSelect
                closeMenuOnSelect={false}
                label={t("tags")}
                isMulti
                name="tag"
                menuPortalTarget={portalTarget}
                styles={portalStyles}
                placeholder={`${t("tags")}...`}
                value={fields.tags}
                noOptionsMessage={() => fields?.tags?.length >= 3 && null}
                promptTextCreator={() => fields?.tags?.length >= 3 && null}
                isValidNewOption={() =>
                  fields?.tags?.length >= 3 ? false : true
                }
                onChange={(tags) => {
                  setFields((draft) => {
                    draft.tags = tags.filter(
                      (tag) => tag.value.trim().length > 0
                    );
                  });
                }}
                options={fields?.tags?.length >= 3 ? [] : labels}
                optionLabel
              />
              {fields?.tags?.length >= 3 && (
                <div className="text-small mt-[1rem] flex items-center gap-2 text-text-secondary">
                  <Icon
                    className="h-5 w-5 fill-text-secondary"
                    variants="info"
                  />
                  {t("maxTagsMessage")}
                </div>
              )}
            </Col>
          </Row>
          <div className="mt-[2rem]">
            <MenuDataTagFields
              kind={MenuDataTagKind.PRODUCT}
              defaultValue={defaultValue}
              showSize
              showVariants
            />
          </div>
        </ProductFormSection>
        <ProductFormSection
          icon="highlighter"
          title={t("menu.productPage.appearance")}
          description={t("menu.productPage.appearanceHint")}
        >
          <div className="flex flex-wrap gap-16">
            <Controller
              name="color"
              defaultValue={defaultValue?.color || ""}
              control={control}
              render={({ onChange, value, name }) => (
                <div>
                  <InputColor
                    showLabel
                    hideTextfield
                    name={name}
                    errors={errors}
                    label={t("backGroundColor")}
                    onChange={(value) => onChange(value)}
                    value={value}
                  />
                </div>
              )}
            />
            <Controller
              name="text_color"
              defaultValue={defaultValue?.text_color || ""}
              control={control}
              render={({ onChange, value, name }) => (
                <div>
                  <InputColor
                    showLabel
                    hideTextfield
                    name={name}
                    errors={errors}
                    label={t("textColor")}
                    onChange={(value) => onChange(value)}
                    value={value}
                  />
                </div>
              )}
            />
          </div>
        </ProductFormSection>
        <ProductFormSection
          icon="utensils"
          title={t("menu.productPage.extras")}
          description={t("menu.productPage.extrasHint")}
        >
          <div className="mb-[2rem]">
            <SortableSelect
              onSortEnd={({ oldIndex, newIndex }) => {
                const newValue = arrayMove(
                  fields.garnishes,
                  oldIndex,
                  newIndex
                );
                setFields((draft) => {
                  draft.garnishes = newValue;
                });
              }}
              menuPortalTarget={portalTarget}
              styles={portalStyles}
              isMulti
              label={t("menu.garnishes")}
              name="garnishes"
              placeholder={`${t("menu.garnishes")}....`}
              getOptionLabel={(option) => getLabel(option)}
              getOptionValue={(option) => option.item_id}
              value={fields.garnishes}
              onChange={(v) => {
                setFields((draft) => {
                  draft.garnishes = v;
                });
              }}
              isLoading={isLoading}
              options={garnishes}
            />
          </div>
          <Select
            isMulti
            menuPortalTarget={portalTarget}
            styles={portalStyles}
            label={t("recommendedProduct")}
            name="recommendedProduct"
            placeholder={`${t("recommendedProduct")}...`}
            getOptionLabel={(option) =>
              getFieldByLang(option.name) || "Geen product naam"
            }
            value={fields.product}
            onChange={(v) =>
              setFields((draft) => {
                draft.product = v;
              })
            }
            options={products?.map((product) => ({
              ...product,
              label: product.name,
              value: product.item_id,
            }))}
            optionLabel
            isClearable
            isLoading={productIsLoading}
          />
        </ProductFormSection>
      </div>
    </div>
  );
};

export default ProductModalStandard;
